import { AxiosInstance } from "axios";
import { SpecificDatePayload } from "../types/common/common-payload";
import { DeliveryDto, DeliveryPageDto } from "../types/delivery/delivery-dto";
import {
    CommentDeliveriesPayload,
    CreateDeliveryPayload,
    RecentLocationByDeliveryPayload,
    RecommandDeliveriesPayload,
    SearchDeliveryPageByPeriodPayload,
    UpdateDeliveryPayload,
} from "../types/delivery/delivery-payload";
import { IdDto } from "../types/id/id-dto";
import { IdsDto } from "../types/id/ids-dto";
import { ongBaseApi } from "./ong-base-api";
import { OngBaseResponse } from "./ong-base-response";
import { DeliveryAllRequest } from "../types/delivery/request/delivery-all-request";

export enum DeliveryApiUrl {
    BaseDeliveries = "/deliveries",
    Create = "/delivery",
    CreateBulk = "/deliveries",
    Update = "/delivery",
    CommentDeliveries = "/v2/deliveries/comment",
    Delete = "/delivery",
    DeleteAll = "/deliveries",
    GetById = "/v2/delivery",
    Search = "/v2/deliveries",
    SearchAll = "/v2/deliveries/all",
    RecentPickUpLocation = "/v2/deliveries/recent/pick-up",
    RecentDropOffLocation = "/v2/deliveries/recent/drop-off",
    Recommand = "/recommand",
}

class DeliveryApi {
    private _api: AxiosInstance;

    constructor(api: AxiosInstance) {
        this._api = api;
    }

    async create(
        payload: CreateDeliveryPayload
    ): Promise<OngBaseResponse<IdDto>> {
        return await this._api.post(DeliveryApiUrl.Create, payload);
    }

    async createBulk(
        payload: CreateDeliveryPayload[]
    ): Promise<OngBaseResponse<IdsDto>> {
        return await this._api.post(DeliveryApiUrl.CreateBulk, payload, {
            timeout: 120000
        });
    }

    async update(
        id: string,
        payload: UpdateDeliveryPayload
    ): Promise<OngBaseResponse<null>> {
        return await this._api.patch(`${DeliveryApiUrl.Update}/${id}`, payload);
    }

    async delete(id: string): Promise<OngBaseResponse<null>> {
        return await this._api.delete(`${DeliveryApiUrl.Delete}/${id}`);
    }

    async deleteAll(specificDate: string): Promise<OngBaseResponse<null>> {
        return await this._api.delete(
            `${DeliveryApiUrl.DeleteAll}/${specificDate}`
        );
    }

    async getById(id: string): Promise<OngBaseResponse<DeliveryDto>> {
        return await this._api.get(`${DeliveryApiUrl.GetById}/${id}`);
    }

    async search(
        payload: SearchDeliveryPageByPeriodPayload
    ): Promise<OngBaseResponse<DeliveryPageDto>> {
        return await this._api.get(DeliveryApiUrl.Search, {
            params: payload,
        });
    }

    async searchAll(
        payload: DeliveryAllRequest
    ): Promise<OngBaseResponse<DeliveryPageDto>> {
        return await this._api.get(DeliveryApiUrl.SearchAll, { params: payload });
    }

    async searchRecentPickUpLocationByDelivery(
        payload: RecentLocationByDeliveryPayload
    ): Promise<OngBaseResponse<DeliveryPageDto>> {
        return await this._api.get(DeliveryApiUrl.RecentPickUpLocation, {
            params: payload,
        });
    }

    async searchRecentDropOffLocationByDelivery(
        payload: RecentLocationByDeliveryPayload
    ): Promise<OngBaseResponse<DeliveryPageDto>> {
        return await this._api.get(DeliveryApiUrl.RecentDropOffLocation, {
            params: payload,
        });
    }

    async commentDeliveries(
        payload: CommentDeliveriesPayload
    ): Promise<OngBaseResponse<null>> {
        return await this._api.patch(DeliveryApiUrl.CommentDeliveries, payload);
    }

    async recommand(
        id: string,
        payload: RecommandDeliveriesPayload
    ): Promise<OngBaseResponse<IdsDto>> {
        return await this._api.get(
            `${DeliveryApiUrl.BaseDeliveries}/${id}${DeliveryApiUrl.Recommand}`,
            { params: payload }
        );
    }
}

export const deliveryApi = new DeliveryApi(ongBaseApi);
